import { computeRSquared, linSpace, logSpace } from '../utils/math';

export const solowSmithG = (t, b) => {
  if (!Number.isFinite(t)) return null;
  if (Math.abs(b) < 1e-8) return t;
  return (Math.exp(b * t) - 1) / b;
};

export const solowSmithP = (t, h, b) => {
  const G = solowSmithG(t, b);
  if (!Number.isFinite(G) || G < 0) return null;
  return G / (h + G);
};

export const solowSmithPdf = (t, h, b) => {
  if (Math.abs(b) < 1e-8) {
    const denom = h + t;
    return denom > 0 ? h / (denom * denom) : null;
  }
  const expBt = Math.exp(b * t);
  const denom = h + (expBt - 1) / b;
  return denom > 0 ? (h * expBt) / (denom * denom) : null;
};

export const buildSolowSmithEvents = (discoveryCounts, startYear) => {
  const events = discoveryCounts
    .filter(item => item.count > 0)
    .map(item => ({ t: item.year - startYear, count: item.count }));
  const n = events.reduce((acc, item) => acc + item.count, 0);
  return { events, n };
};

export const computeSolowSmithLogL = (events, n, t0, h, b) => {
  const P0 = solowSmithP(t0, h, b);
  if (!(P0 > 0 && P0 < 1)) return null;
  let logL = -n * Math.log(P0);
  for (let i = 0; i < events.length; i += 1) {
    const event = events[i];
    const p = solowSmithPdf(event.t, h, b);
    if (!(p > 0)) return null;
    logL += event.count * Math.log(p);
  }
  return logL;
};

export const sampleSolowSmithTimes = (n, params, t0, randomFn = Math.random) => {
  if (!params) return null;
  const { h, b } = params;
  const P0 = solowSmithP(t0, h, b);
  if (!(P0 > 0 && P0 < 1)) return null;
  const times = [];
  const maxAttempts = Math.max(1000, n * 20);
  let attempts = 0;

  while (times.length < n && attempts < maxAttempts) {
    attempts += 1;
    const u = randomFn() * P0;
    if (!(u > 0 && u < 1)) continue;
    const G = (u * h) / (1 - u);
    let t;
    if (Math.abs(b) < 1e-8) {
      t = G;
    } else {
      const inner = 1 + b * G;
      if (!(inner > 0)) continue;
      t = Math.log(inner) / b;
    }
    if (!Number.isFinite(t)) continue;
    if (t < 0) t = 0;
    if (t > t0) t = t0;
    times.push(t);
  }

  return times.length === n ? times : null;
};

export const simulateDiscoveryYearsFromSolowSmith = (
  n,
  params,
  startYear,
  endYear,
  randomFn = Math.random
) => {
  const t0 = endYear - startYear;
  if (!(t0 > 0)) return null;
  const times = sampleSolowSmithTimes(n, params, t0, randomFn);
  if (!times) return null;
  return times.map((t) => {
    const year = Math.floor(startYear + t);
    return Math.max(startYear, Math.min(endYear, year));
  });
};

export const computeExpectedDiscoveryCounts = (fit, startYear, endYear) => {
  if (!fit || !fit.params || !Number.isFinite(fit.total)) return null;
  const expected = [];
  let previous = 0;
  for (let year = startYear; year <= endYear; year += 1) {
    const t = year - startYear;
    const P = solowSmithP(t, fit.params.h, fit.params.b);
    if (!Number.isFinite(P)) return null;
    const cumulative = fit.total * P;
    const count = Math.max(0, cumulative - previous);
    expected.push({ year, count });
    previous = cumulative;
  }
  return expected;
};

export const fitSolowSmithCore = (discoveryCounts, startYear, endYear, observedPoints) => {
  if (!discoveryCounts || discoveryCounts.length === 0) return null;
  const t0 = endYear - startYear;
  if (!(t0 > 0)) return null;

  const { events, n } = buildSolowSmithEvents(discoveryCounts, startYear);
  if (n < 3) return null;

  const bRange = 4 / t0;
  const bCandidates = linSpace(-bRange, bRange, 41);
  let best = { logL: -Infinity, params: null };

  bCandidates.forEach((b) => {
    const G0 = solowSmithG(t0, b);
    if (!Number.isFinite(G0) || G0 <= 0) return;
    const hCandidates = logSpace(Math.max(G0 / 100, 1e-6), G0 * 100, 30);
    hCandidates.forEach((h) => {
      const logL = computeSolowSmithLogL(events, n, t0, h, b);
      if (logL === null) return;
      if (logL > best.logL) {
        best = { logL, params: { h, b } };
      }
    });
  });

  if (!best.params) return null;

  const bStep = (bRange * 2) / Math.max(1, bCandidates.length - 1);
  const refineB = linSpace(best.params.b - bStep * 2, best.params.b + bStep * 2, 31);
  const refineH = logSpace(Math.max(best.params.h / 3, 1e-6), best.params.h * 3, 30);

  refineB.forEach((b) => {
    refineH.forEach((h) => {
      const logL = computeSolowSmithLogL(events, n, t0, h, b);
      if (logL === null) return;
      if (logL > best.logL) {
        best = { logL, params: { h, b } };
      }
    });
  });

  const P0 = solowSmithP(t0, best.params.h, best.params.b);
  if (!(P0 > 0 && P0 < 1)) return null;
  const undiscovered = n * (1 - P0) / P0;
  const total = n + undiscovered;

  let rSquared = null;
  if (observedPoints && observedPoints.length > 0) {
    const predicted = observedPoints.map((point) => {
      const t = point.x - startYear;
      const P = solowSmithP(t, best.params.h, best.params.b);
      return Number.isFinite(P) ? total * P : null;
    }).filter(value => Number.isFinite(value));
    if (predicted.length === observedPoints.length) {
      rSquared = computeRSquared(observedPoints.map(point => point.y), predicted);
    }
  }

  return {
    params: best.params,
    total,
    undiscovered,
    rSquared,
    P0,
    n,
    logL: best.logL,
  };
};

export const profileSolowSmithCI = (discoveryCounts, startYear, endYear, baseFit) => {
  if (!baseFit || !baseFit.params) return null;
  const t0 = endYear - startYear;
  if (!(t0 > 0)) return null;
  const { events, n } = buildSolowSmithEvents(discoveryCounts, startYear);
  if (n < 3) return null;

  const bRange = 4 / t0;
  const bCandidates = linSpace(-bRange, bRange, 61);
  const bStep = (bRange * 2) / Math.max(1, bCandidates.length - 1);

  const profileLogL = (P0) => {
    if (!(P0 > 0 && P0 < 1)) return -Infinity;
    let bestLogL = -Infinity;
    let bestB = null;
    bCandidates.forEach((b) => {
      const G0 = solowSmithG(t0, b);
      if (!Number.isFinite(G0) || G0 <= 0) return;
      const h = (G0 * (1 - P0)) / P0;
      if (!(h > 0)) return;
      const logL = computeSolowSmithLogL(events, n, t0, h, b);
      if (logL === null) return;
      if (logL > bestLogL) {
        bestLogL = logL;
        bestB = b;
      }
    });
    if (!Number.isFinite(bestLogL) || bestB === null) return -Infinity;
    const refineB = linSpace(
      Math.max(-bRange, bestB - bStep * 2),
      Math.min(bRange, bestB + bStep * 2),
      31
    );
    refineB.forEach((b) => {
      const G0 = solowSmithG(t0, b);
      if (!Number.isFinite(G0) || G0 <= 0) return;
      const h = (G0 * (1 - P0)) / P0;
      if (!(h > 0)) return;
      const logL = computeSolowSmithLogL(events, n, t0, h, b);
      if (logL === null) return;
      if (logL > bestLogL) bestLogL = logL;
    });
    return bestLogL;
  };

  const baseP0 = baseFit.P0;
  if (!(baseP0 > 0 && baseP0 < 1)) return null;
  const maxLogL = profileLogL(baseP0);
  if (!Number.isFinite(maxLogL)) return null;
  const threshold = maxLogL - 1.9205;

  const oddsFromP0 = (P0) => P0 / (1 - P0);
  const p0FromOdds = (odds) => odds / (1 + odds);
  const clampOdds = (odds) => Math.min(1e12, Math.max(1e-12, odds));

  const findBoundary = (direction) => {
    const step = 1.35;
    const maxSteps = 60;
    let lowOdds = oddsFromP0(baseP0);
    let highOdds = lowOdds;
    let highLogL = maxLogL;

    for (let i = 0; i < maxSteps; i += 1) {
      const nextOdds = clampOdds(direction === 'lower' ? highOdds / step : highOdds * step);
      if (nextOdds === highOdds) break;
      highOdds = nextOdds;
      highLogL = profileLogL(p0FromOdds(highOdds));
      if (highLogL < threshold) {
        const belowOdds = highOdds;
        const aboveOdds = lowOdds;
        let lower = Math.min(belowOdds, aboveOdds);
        let upper = Math.max(belowOdds, aboveOdds);
        for (let j = 0; j < 40; j += 1) {
          const mid = Math.sqrt(lower * upper);
          const midLogL = profileLogL(p0FromOdds(mid));
          if (midLogL >= threshold) {
            if (direction === 'lower') upper = mid;
            else lower = mid;
          } else if (direction === 'lower') {
            lower = mid;
          } else {
            upper = mid;
          }
        }
        return p0FromOdds(direction === 'lower' ? upper : lower);
      }
    }
    return null;
  };

  const lowerP0 = findBoundary('lower');
  const upperP0 = findBoundary('upper');
  if (!(lowerP0 > 0 && upperP0 > 0)) return null;

  return {
    lower: n / upperP0,
    upper: n / lowerP0,
    samples: null,
  };
};
